// Fuzzy-match a Moroccan address string against the AMEEX cities catalogue.
// Returns the best matching city id + name, or null when no token in the
// address matches any city name with high enough confidence.
//
// The admin's customer address looks like "kenitra maamora" or
// "Sidi Maarouf, Casablanca" — we tokenize, normalise (strip accents +
// lowercase), and look for the LONGEST city name whose words all appear in
// the address tokens. Length-weighting prevents short matches like "Ben"
// from beating "Beni Mellal" when both are present.

import citiesRaw from '@/data/ameex-cities.json';

type City = { id: number; name: string; code: string | null; zone: string | null; d_fees: number | null };
const CITIES = citiesRaw as unknown as City[];

function normalise(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // strip diacritics
    .replace(/[^a-z0-9\s]/g, ' ')    // strip punctuation
    .replace(/\s+/g, ' ')
    .trim();
}

export interface CityMatch {
  id: number;
  name: string;
}

export function detectCityFromAddress(address: string | null | undefined): CityMatch | null {
  if (!address) return null;
  const haystack = normalise(address);
  if (!haystack) return null;

  // Score each city: number of city-name words that appear as standalone
  // tokens in the address, weighted by the length of the city name.
  // Higher = better match. Ties broken by longer original city name (more
  // specific). We require ALL words of the city to appear in the address
  // tokens to avoid spurious matches (e.g. "Ben" matching "Beni Mellal").
  const haystackTokens = new Set(haystack.split(' '));
  let best: { score: number; city: City } | null = null;

  for (const city of CITIES) {
    const cityWords = normalise(city.name).split(' ').filter(Boolean);
    if (cityWords.length === 0) continue;
    const allWordsPresent = cityWords.every((w) => haystackTokens.has(w));
    if (!allWordsPresent) continue;

    // Score by the total length of matched characters. Multi-word city
    // names (e.g. "Beni Mellal", "Ait Yadine") win over short single
    // words ("Sale") when both are present in the address.
    const score = cityWords.reduce((s, w) => s + w.length, 0);
    if (!best || score > best.score) {
      best = { score, city };
    }
  }

  if (!best) return null;
  return { id: best.city.id, name: best.city.name };
}
